import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Tooltip,
  Badge,
  Autocomplete,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchLLMMemoriesByCustomerId,
  updateLLMMemoryStatus,
} from "../dataaccess/fetchLLMMemories";
import { createLLMMemoryAfterAdd } from "../dataaccess/createLLMMemory";
import { updateLLMMemoryAfterEdit } from "../dataaccess/updateLLMMemoryAfterEdit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { LLMKnowledgeGraphStatus, LLMMemoryEntityType } from "../API";
import { useLLM } from "llmasaservice-client";
import { createLLMKnowledgeGraphAfterAdd } from "../dataaccess/createLLMKnowledgeGraph";
import { fetchLLMKnowledgeGraphsByCustomerId } from "../dataaccess/fetchLLMKnowledgeGraphs";
import { useUser } from "../contexts/useUser";

interface MemoryAndKnowledgeGraphProps {
  selectedCustomer: any | null;
  conversation?: any | null;
}

const MemoryAndKnowledgeGraph: React.FC<MemoryAndKnowledgeGraphProps> = ({
  selectedCustomer,
  conversation,
}) => {
  const queryClient = useQueryClient();

  const { project } = useUser();

  // Memory dialog state
  const [memoryDialogOpen, setMemoryDialogOpen] = React.useState(false);
  const [editingMemory, setEditingMemory] = React.useState<any | null>(null);
  const [memoryForm, setMemoryForm] = React.useState({
    key: "",
    data: "",
    memoryType: "FACT",
    tags: "",
  });

  const [memoryFilter, setMemoryFilter] = React.useState("ACTIVE"); // all, active, paused, deleted
  const [tabValue, setTabValue] = React.useState(0); // 0 = memories, 1 = links, 2 = knowledge graph
  const [analyzingMemories, setAnalyzingMemories] = React.useState(false);
  const [memorySuggestions, setMemorySuggestions] = React.useState<
    Record<string, any>
  >({});
  const [generatingGraph, setGeneratingGraph] = React.useState(false);
  const [knowledgeGraph, setKnowledgeGraph] = React.useState<any | null>(null);
  const [showVisualization, setShowVisualization] = React.useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  // Knowledge graph saving state
  const [savingGraph, setSavingGraph] = React.useState(false);
  const [savedGraphs, setSavedGraphs] = React.useState<any[]>([]);
  const [selectedGraphTab, setSelectedGraphTab] = React.useState<number>(0); // 0 = current/new graph, 1+ = saved versions
  const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] =
    React.useState(false);

  // Text extraction dialog state
  const [textExtractionDialogOpen, setTextExtractionDialogOpen] =
    React.useState(false);
  const [extractionText, setExtractionText] = React.useState("");
  const [analyzingText, setAnalyzingText] = React.useState(false);
  const [extractedMemories, setExtractedMemories] = React.useState<any[]>([]);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const { send, idle } = useLLM({ project_id: project?.id || "", url: url });

  const {
    data: memoriesData,
    isLoading: memoriesLoading,
    isError: memoriesError,
  } = useQuery({
    queryKey: ["memories", selectedCustomer?.id],
    queryFn: () => fetchLLMMemoriesByCustomerId(selectedCustomer?.id ?? ""),
    enabled: selectedCustomer?.id ? true : false,
  });

  const {
    data: savedGraphsData,
    isLoading: savedGraphsLoading,
    isError: savedGraphsError,
  } = useQuery({
    queryKey: ["knowledgeGraphs", selectedCustomer?.id],
    queryFn: () =>
      fetchLLMKnowledgeGraphsByCustomerId(selectedCustomer?.id ?? ""),
    enabled: selectedCustomer?.id ? true : false,
  });

  const updateMemoryMutation = useMutation({
    mutationFn: updateLLMMemoryStatus,
    onSuccess: () => {
      // Invalidate and refetch memories
      queryClient.invalidateQueries({
        queryKey: ["memories", selectedCustomer?.id],
      });
    },
  });

  const createMemoryMutation = useMutation({
    mutationFn: createLLMMemoryAfterAdd,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["memories", selectedCustomer?.id],
      });
      handleCloseMemoryDialog();
    },
  });

  const editMemoryMutation = useMutation({
    mutationFn: updateLLMMemoryAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["memories", selectedCustomer?.id],
      });
      handleCloseMemoryDialog();
    },
  });

  const saveKnowledgeGraphMutation = useMutation({
    mutationFn: createLLMKnowledgeGraphAfterAdd,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["knowledgeGraphs", selectedCustomer?.id],
      });
      setSaveConfirmDialogOpen(false);
      setSavingGraph(false);
    },
  });

  React.useEffect(() => {
    setKnowledgeGraph(null);
    setShowVisualization(false);
    setMemorySuggestions({});
    setSelectedGraphTab(0);
  }, [selectedCustomer?.id]);

  React.useEffect(() => {
    if (savedGraphsData?.data?.knowledgeGraphsByCustomerIdAndCreatedAt?.items) {
      setSavedGraphs(
        savedGraphsData.data.knowledgeGraphsByCustomerIdAndCreatedAt.items
      );
    }
  }, [savedGraphsData]);

  const memories = memoriesData?.data?.memoryByCustomerIdAndKey?.items ?? [];

  const filteredMemories = memories.filter((memory: any) => {
    if (memoryFilter === "all") return true;
    return memory.status === memoryFilter.toUpperCase();
  });

  const handleMemoryStatusUpdate = (memoryId: string, status: string) => {
    updateMemoryMutation.mutate({
      id: memoryId,
      status: status,
    });
  };

  const handleOpenMemoryDialog = (memory?: any) => {
    if (memory) {
      setEditingMemory(memory);
      setMemoryForm({
        key: memory.key || "",
        data: memory.data || "",
        memoryType: memory.memoryType || "FACT",
        tags: memory.tags ? memory.tags.join(", ") : "",
      });
    } else {
      setEditingMemory(null);
      setMemoryForm({
        key: "",
        data: "",
        memoryType: "FACT",
        tags: "",
      });
    }
    setMemoryDialogOpen(true);
  };

  const handleCloseMemoryDialog = () => {
    setMemoryDialogOpen(false);
    setEditingMemory(null);
    setMemoryForm({
      key: "",
      data: "",
      memoryType: "FACT",
      tags: "",
    });
  };

  const handleSaveMemory = () => {
    const tagsArray = memoryForm.tags
      ? memoryForm.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)
      : [];

    if (editingMemory) {
      // Update existing memory
      editMemoryMutation.mutate({
        id: editingMemory.id,
        key: memoryForm.key,
        data: memoryForm.data,
        memoryType: memoryForm.memoryType as any,
        tags: tagsArray,
      });
    } else {
      // Create new memory
      createMemoryMutation.mutate({
        projectId: project?.id || "",
        customerId: selectedCustomer?.id || "",
        entityType: LLMMemoryEntityType.CUSTOMER,
        status: "ACTIVE",
        key: memoryForm.key,
        data: memoryForm.data,
        memoryType: memoryForm.memoryType as any,
        tags: tagsArray,
      });
    }
  };

  const analyzeMemories = async () => {
    if (!selectedCustomer || memories.length === 0) return;

    setAnalyzingMemories(true);

    const memoriesText = memories
      .map(
        (memory) =>
          `Memory ID: ${memory?.id}\nKey: ${memory?.key}\nData: ${
            memory?.data
          }\nType: ${memory?.memoryType}\nCurrent Tags: ${
            memory?.tags?.join(",") || "none"
          }\n---`
      )
      .join("\n\n");

    const prompt = `{{system_analyzememories}}
${memoriesText}`;

    try {
      const results = await send(prompt, [], [], false, false);
      console.log("Analyze memories LLM response:", results);

      // Extract JSON from response using regex
      let cleanedResults = results as string;

      // Look for JSON content between ```json and ``` or just standalone JSON objects
      const jsonMatch = cleanedResults.match(
        /```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/
      );

      if (jsonMatch) {
        // Use the first capturing group (between ```json and ```) or the second (standalone JSON)
        cleanedResults = jsonMatch[1] || jsonMatch[2];
      } else {
        // Fallback: try to find the first complete JSON object
        const objectStart = cleanedResults.indexOf("{");
        const objectEnd = cleanedResults.lastIndexOf("}");
        if (objectStart !== -1 && objectEnd !== -1 && objectEnd > objectStart) {
          cleanedResults = cleanedResults.substring(objectStart, objectEnd + 1);
        }
      }

      cleanedResults = cleanedResults.trim();

      const analysis = JSON.parse(cleanedResults);
      setMemorySuggestions(analysis.memorySuggestions || {});
      setAnalyzingMemories(false);
    } catch (error) {
      console.error("Error analyzing memories:", error);
      setAnalyzingMemories(false);
    }
  };

  const applySuggestion = (memoryId: string, type: "tags" | "data") => {
    const suggestion = memorySuggestions[memoryId];
    if (!suggestion) return;

    const memory = memories.find((m) => m?.id === memoryId);
    if (!memory) return;

    if (type === "tags") {
      const currentTags = memory.tags || [];
      const newTags = [...currentTags, ...suggestion.suggestedTags];
      editMemoryMutation.mutate({
        id: memoryId,
        key: memory.key,
        data: memory.data,
        memoryType: memory.memoryType,
        tags: newTags,
      });
    } else if (type === "data") {
      editMemoryMutation.mutate({
        id: memoryId,
        key: memory.key,
        data: suggestion.suggestedDataImprovement,
        memoryType: memory.memoryType,
        tags: memory.tags || [],
      });
    }

    // Only remove the specific suggestion type that was applied
    setMemorySuggestions((prev) => {
      const updated = { ...prev };
      if (updated[memoryId]) {
        if (type === "tags") {
          delete updated[memoryId].suggestedTags;
        } else if (type === "data") {
          delete updated[memoryId].suggestedDataImprovement;
        }

        // If no suggestions remain for this memory, remove the entire entry
        if (
          !updated[memoryId].suggestedTags &&
          !updated[memoryId].suggestedDataImprovement
        ) {
          delete updated[memoryId];
        }
      }
      return updated;
    });
  };

  const discardSuggestion = (memoryId: string, type?: "tags" | "data") => {
    setMemorySuggestions((prev) => {
      const updated = { ...prev };
      if (updated[memoryId]) {
        if (type === "tags") {
          delete updated[memoryId].suggestedTags;
        } else if (type === "data") {
          delete updated[memoryId].suggestedDataImprovement;
        } else {
          // If no type specified, remove all suggestions for this memory
          delete updated[memoryId];
          return updated;
        }

        // If no suggestions remain for this memory, remove the entire entry
        if (
          !updated[memoryId].suggestedTags &&
          !updated[memoryId].suggestedDataImprovement
        ) {
          delete updated[memoryId];
        }
      }
      return updated;
    });
  };

  const generateKnowledgeGraph = async () => {
    if (!selectedCustomer || memories.length === 0) return;

    setGeneratingGraph(true);

    const memoriesText = memories
      .map(
        (memory) =>
          `Memory ID: ${memory?.id}\nKey: ${memory?.key}\nData: ${
            memory?.data
          }\nType: ${memory?.memoryType}\nTags: ${
            memory?.tags?.join(",") || "none"
          }\n---`
      )
      .join("\n\n");

    const prompt = `{{system_knowledgegraph}}
${memoriesText}`;

    try {
      const results = await send(prompt, [], [], false, false);
      console.log("Knowledge Graph LLM response:", results);

      // Strip out markdown code block formatting
      let cleanedResults = results as string;
      cleanedResults = cleanedResults.replace(/```json\s*/g, "");
      cleanedResults = cleanedResults.replace(/```\s*/g, "");
      cleanedResults = cleanedResults.trim();

      const graph = JSON.parse(cleanedResults);
      setKnowledgeGraph(graph);
      setGeneratingGraph(false);
    } catch (error) {
      console.error("Error generating knowledge graph:", error);
      setGeneratingGraph(false);
    }
  };

  React.useEffect(() => {
    if (showVisualization && knowledgeGraph) {
      createD3Visualization();
    }
  }, [showVisualization, knowledgeGraph]);

  const createD3Visualization = () => {
    // Clear any existing visualization
    const container = document.getElementById("d3-graph-container");
    if (!container) return;

    container.innerHTML = "";

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Create SVG with zoom and pan functionality
    const svg = document.createElement("div");
    svg.innerHTML = `
      <svg width="${width}" height="${height}" id="knowledge-graph-svg" style="cursor: grab;">
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" 
                  refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
          </marker>
        </defs>
        <g id="zoom-group"></g>
      </svg>
    `;
    container.appendChild(svg.firstElementChild as Element);

    // Zoom and pan state
    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    let isDragging = false;
    let lastMouseX = 0;
    let lastMouseY = 0;
    let draggedNode: any = null;
    let isNodeDragging = false;

    const svgElement = document.getElementById("knowledge-graph-svg") as any;
    const zoomGroup = document.getElementById("zoom-group") as any;

    // Apply transform
    const updateTransform = () => {
      zoomGroup.setAttribute(
        "transform",
        `translate(${translateX}, ${translateY}) scale(${scale})`
      );
    };

    // Mouse wheel zoom
    svgElement.addEventListener("wheel", (e: WheelEvent) => {
      e.preventDefault();
      const rect = svgElement.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.1, Math.min(5, scale * scaleFactor));

      // Zoom towards mouse position
      const scaleChange = newScale / scale;
      translateX = mouseX - (mouseX - translateX) * scaleChange;
      translateY = mouseY - (mouseY - translateY) * scaleChange;
      scale = newScale;

      updateTransform();
    });

    // Helper function to get mouse position relative to zoom group
    const getMousePos = (e: MouseEvent) => {
      const rect = svgElement.getBoundingClientRect();
      const x = (e.clientX - rect.left - translateX) / scale;
      const y = (e.clientY - rect.top - translateY) / scale;
      return { x, y };
    };

    // Helper function to find node at position
    const findNodeAtPosition = (x: number, y: number) => {
      return nodes.find((node: any) => {
        const textLength = node.label.length;
        const ellipseWidth = Math.max(60, textLength * 8);
        const ellipseHeight = 35;
        const dx = x - node.x;
        const dy = y - node.y;
        return (
          (dx * dx) / (ellipseWidth / 2) ** 2 +
            (dy * dy) / (ellipseHeight / 2) ** 2 <=
          1
        );
      });
    };

    // Mouse drag pan and node dragging
    svgElement.addEventListener("mousedown", (e: MouseEvent) => {
      if (e.button === 0) {
        // Left mouse button
        const mousePos = getMousePos(e);
        const nodeAtPos = findNodeAtPosition(mousePos.x, mousePos.y);

        if (nodeAtPos) {
          // Start node dragging
          isNodeDragging = true;
          draggedNode = nodeAtPos;
          svgElement.style.cursor = "grabbing";
          e.preventDefault();
        } else {
          // Start pan dragging
          isDragging = true;
          lastMouseX = e.clientX;
          lastMouseY = e.clientY;
          svgElement.style.cursor = "grabbing";
        }
      }
    });

    svgElement.addEventListener("mousemove", (e: MouseEvent) => {
      if (isNodeDragging && draggedNode) {
        const mousePos = getMousePos(e);
        draggedNode.x = mousePos.x;
        draggedNode.y = mousePos.y;
        draggedNode.vx = 0; // Stop velocity
        draggedNode.vy = 0;
        updateVisualization();
      } else if (isDragging) {
        const deltaX = e.clientX - lastMouseX;
        const deltaY = e.clientY - lastMouseY;
        translateX += deltaX;
        translateY += deltaY;
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
        updateTransform();
      } else {
        // Update cursor based on whether we're over a node
        const mousePos = getMousePos(e);
        const nodeAtPos = findNodeAtPosition(mousePos.x, mousePos.y);
        svgElement.style.cursor = nodeAtPos ? "grab" : "grab";
      }
    });

    svgElement.addEventListener("mouseup", () => {
      isDragging = false;
      isNodeDragging = false;
      draggedNode = null;
      svgElement.style.cursor = "grab";
    });

    svgElement.addEventListener("mouseleave", () => {
      isDragging = false;
      isNodeDragging = false;
      draggedNode = null;
      svgElement.style.cursor = "grab";
    });

    // Double-click to reset zoom
    svgElement.addEventListener("dblclick", () => {
      scale = 1;
      translateX = 0;
      translateY = 0;
      updateTransform();
    });

    // Prepare data
    const nodes =
      knowledgeGraph.entities?.map((entity: any) => ({
        id: entity.id,
        label: entity.label,
        type: entity.type,
        description: entity.description,
        x: Math.random() * (width - 200) + 100,
        y: Math.random() * (height - 200) + 100,
        vx: 0,
        vy: 0,
      })) || [];

    const links =
      knowledgeGraph.relationships?.map((rel: any) => ({
        source: rel.source,
        target: rel.target,
        label: rel.label || rel.type,
        strength: rel.strength || 1,
      })) || [];

    // Simple force simulation without D3
    const simulation = {
      nodes,
      links,
      alpha: 1,
      alphaDecay: 0.015, // Slower decay for more settling time
      velocityDecay: 0.6, // Higher decay to reduce oscillation

      tick() {
        // Apply forces
        this.nodes.forEach((node: any) => {
          // Skip physics for dragged nodes
          if (node === draggedNode) return;

          // Center force (weaker)
          node.vx += (width / 2 - node.x) * 0.0005;
          node.vy += (height / 2 - node.y) * 0.0005;

          // Repulsion between nodes (stronger)
          this.nodes.forEach((other: any) => {
            if (node !== other) {
              const dx = node.x - other.x;
              const dy = node.y - other.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance < 200) {
                // Increased repulsion range
                const force = ((200 - distance) / 200) * 3; // Stronger repulsion
                node.vx += dx * force * 0.02;
                node.vy += dy * force * 0.02;
              }
            }
          });
        });

        // Link forces (adjusted for better spacing)
        this.links.forEach((link: any) => {
          const source = this.nodes.find((n: any) => n.id === link.source);
          const target = this.nodes.find((n: any) => n.id === link.target);
          if (source && target) {
            const dx = target.x - source.x;
            const dy = target.y - source.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const targetDistance = 200; // Increased target distance for less overlap
            const force = ((distance - targetDistance) / distance) * 0.05; // Reduced force strength

            if (source !== draggedNode) {
              source.vx += dx * force;
              source.vy += dy * force;
            }
            if (target !== draggedNode) {
              target.vx -= dx * force;
              target.vy -= dy * force;
            }
          }
        });

        // Update positions
        this.nodes.forEach((node: any) => {
          if (node === draggedNode) return; // Skip dragged nodes

          node.vx *= this.velocityDecay;
          node.vy *= this.velocityDecay;
          node.x += node.vx;
          node.y += node.vy;

          // Keep nodes in bounds with padding
          const padding = 50;
          node.x = Math.max(padding, Math.min(width - padding, node.x));
          node.y = Math.max(padding, Math.min(height - padding, node.y));
        });

        this.alpha *= 1 - this.alphaDecay;
        return this.alpha > 0.001;
      },
    };

    // Animation loop
    const animate = () => {
      if (simulation.tick() || isNodeDragging) {
        updateVisualization();
        requestAnimationFrame(animate);
      }
    };

    const updateVisualization = () => {
      const zoomGroup = document.getElementById("zoom-group") as any;
      if (!zoomGroup) return;

      // Clear existing elements
      zoomGroup.innerHTML = "";

      // Draw links with improved label positioning
      links.forEach((link: any) => {
        const source = nodes.find((n: any) => n.id === link.source);
        const target = nodes.find((n: any) => n.id === link.target);
        if (source && target) {
          const line = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "line"
          );
          line.setAttribute("x1", source.x.toString());
          line.setAttribute("y1", source.y.toString());
          line.setAttribute("x2", target.x.toString());
          line.setAttribute("y2", target.y.toString());
          line.setAttribute("stroke", "#666");
          line.setAttribute("stroke-width", "2");
          line.setAttribute("marker-end", "url(#arrowhead)");
          zoomGroup.appendChild(line);

          // Add link label with background for better readability
          const midX = (source.x + target.x) / 2;
          const midY = (source.y + target.y) / 2;

          // Calculate offset to avoid node overlap
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const offsetX = (-dy / length) * 15; // Perpendicular offset
          const offsetY = (dx / length) * 15;

          // Background rectangle for text
          const textBg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "rect"
          );
          const textWidth = link.label.length * 6;
          textBg.setAttribute("x", (midX + offsetX - textWidth / 2).toString());
          textBg.setAttribute("y", (midY + offsetY - 8).toString());
          textBg.setAttribute("width", textWidth.toString());
          textBg.setAttribute("height", "16");
          textBg.setAttribute("fill", "rgba(0, 0, 0, 0)");
          textBg.setAttribute("stroke", "none");
          textBg.setAttribute("rx", "2");
          zoomGroup.appendChild(textBg);

          const text = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "text"
          );
          text.setAttribute("x", (midX + offsetX).toString());
          text.setAttribute("y", (midY + offsetY + 3).toString());
          text.setAttribute("text-anchor", "middle");
          text.setAttribute("font-size", "10");
          text.setAttribute("fill", "#666");
          text.setAttribute("font-weight", "bold");
          text.setAttribute("style", "cursor: pointer;");
          text.textContent = link.label;

          // Add tooltip to link text
          const linkTitle = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "title"
          );
          linkTitle.textContent = `Relationship: ${link.label}\nFrom: ${
            source.label
          }\nTo: ${target.label}${
            link.strength
              ? `\nStrength: ${Math.round(link.strength * 100)}%`
              : ""
          }`;
          text.appendChild(linkTitle);

          zoomGroup.appendChild(text);
        }
      });

      // Draw nodes as ellipses
      nodes.forEach((node: any) => {
        const textLength = node.label.length;
        const ellipseWidth = Math.max(60, textLength * 8);
        const ellipseHeight = 35;

        // Create group for node and tooltip
        const group = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "g"
        );
        group.setAttribute("style", "cursor: grab;");

        const ellipse = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "ellipse"
        );
        ellipse.setAttribute("cx", node.x.toString());
        ellipse.setAttribute("cy", node.y.toString());
        ellipse.setAttribute("rx", (ellipseWidth / 2).toString());
        ellipse.setAttribute("ry", (ellipseHeight / 2).toString());
        ellipse.setAttribute("fill", getNodeColor(node.type));
        ellipse.setAttribute("stroke", "#333");
        ellipse.setAttribute("stroke-width", "2");

        // Highlight dragged node
        if (node === draggedNode) {
          ellipse.setAttribute("stroke", "#ff6b6b");
          ellipse.setAttribute("stroke-width", "3");
        }

        // Add tooltip functionality
        const title = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "title"
        );
        title.textContent = `${node.label}\nType: ${node.type}\nDescription: ${
          node.description || "No description"
        }\n\nTip: Drag to reposition`;
        ellipse.appendChild(title);

        group.appendChild(ellipse);

        const text = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "text"
        );
        text.setAttribute("x", node.x.toString());
        text.setAttribute("y", (node.y + 4).toString());
        text.setAttribute("text-anchor", "middle");
        text.setAttribute("font-size", "12");
        text.setAttribute("font-weight", "bold");
        text.setAttribute("fill", "white");
        text.setAttribute("pointer-events", "none");

        // Ellipse text if too long
        let displayText = node.label;
        if (textLength > 12) {
          displayText = node.label.substring(0, 12) + "...";
        }
        text.textContent = displayText;

        group.appendChild(text);
        zoomGroup.appendChild(group);
      });
    };

    const getNodeColor = (type: string) => {
      const colors: Record<string, string> = {
        person: "#1976d2",
        company: "#9c27b0",
        technology: "#2196f3",
        preference: "#4caf50",
        goal: "#ff9800",
        concept: "#607d8b",
      };
      return colors[type] || "#757575";
    };

    // Start animation
    animate();
  };

  const exportVisualizationAsSVG = () => {
    const svgElement = document.getElementById("knowledge-graph-svg");
    if (!svgElement || !knowledgeGraph) return;

    // Clone the SVG to avoid modifying the original
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Add some styling for export
    const style = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "style"
    );
    style.textContent = `
      text { font-family: Arial, sans-serif; }
      .export-title { font-size: 16px; font-weight: bold; fill: #333; }
    `;
    svgClone.insertBefore(style, svgClone.firstChild);

    // Add title
    const titleText = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "text"
    );
    titleText.setAttribute("x", "10");
    titleText.setAttribute("y", "25");
    titleText.setAttribute("class", "export-title");
    titleText.textContent = `Knowledge Graph - ${
      selectedCustomer?.name || "Customer"
    }`;
    svgClone.appendChild(titleText);

    // Create blob and download
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `knowledge-graph-${
      selectedCustomer?.name || "customer"
    }.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportVisualizationAsPNG = () => {
    const svgElement = document.getElementById("knowledge-graph-svg");
    if (!svgElement || !knowledgeGraph) return;

    const svgClone = svgElement.cloneNode(true) as SVGElement;
    const style = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "style"
    );
    style.textContent = `
      text { font-family: Arial, sans-serif; }
      .export-title { font-size: 16px; font-weight: bold; fill: #333; }
    `;
    svgClone.insertBefore(style, svgClone.firstChild);

    const titleText = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "text"
    );
    titleText.setAttribute("x", "10");
    titleText.setAttribute("y", "25");
    titleText.setAttribute("class", "export-title");
    titleText.textContent = `Knowledge Graph - ${
      selectedCustomer?.name || "Customer"
    }`;
    svgClone.appendChild(titleText);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      // Fill with white background
      ctx!.fillStyle = "white";
      ctx!.fillRect(0, 0, canvas.width, canvas.height);

      ctx!.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `knowledge-graph-${
            selectedCustomer?.name || "customer"
          }.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, "image/png");
    };

    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  };

  const exportGraphData = () => {
    if (!knowledgeGraph || !selectedCustomer) return;

    const exportData = {
      customer: {
        id: selectedCustomer.id,
        name: selectedCustomer.name,
        status: selectedCustomer.status,
        exportDate: new Date().toISOString(),
      },
      knowledgeGraph: knowledgeGraph,
      memories: memories
        .filter(
          (memory): memory is NonNullable<typeof memory> => memory !== null
        )
        .map((memory) => ({
          id: memory.id,
          key: memory.key,
          data: memory.data,
          memoryType: memory.memoryType,
          tags: memory.tags,
          status: memory.status,
        })),
      metadata: {
        totalEntities: knowledgeGraph.entities?.length || 0,
        totalRelationships: knowledgeGraph.relationships?.length || 0,
        totalMemories: memories.length,
      },
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `knowledge-graph-data-${
      selectedCustomer.name || "customer"
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveKnowledgeGraph = async () => {
    if (!knowledgeGraph || !selectedCustomer) return;

    setSavingGraph(true);

    try {
      // Calculate next version number
      const nextVersion =
        savedGraphs.length > 0
          ? Math.max(...savedGraphs.map((g) => g.version || 0)) + 1
          : 1;

      // Auto-generate title and description
      const title = `Knowledge Graph v${nextVersion}`;
      const description = `Generated from ${
        memories.length
      } memories on ${new Date().toLocaleDateString()}`;

      // Get memory IDs used for generation - filter out null/undefined and ensure string[] type
      const memoryIds = memories
        .map((m) => m?.id)
        .filter((id): id is string => id != null);

      // Save the new graph
      await saveKnowledgeGraphMutation.mutateAsync({
        projectId: project?.id || "",
        customerId: selectedCustomer.id,
        status: LLMKnowledgeGraphStatus.ACTIVE,
        version: nextVersion,
        title,
        description,
        graphData: JSON.stringify(knowledgeGraph),
        memoryCount: memories.length,
        generatedFromMemoryIds: memoryIds,
      });
    } catch (error) {
      console.error("Error saving knowledge graph:", error);
      setSavingGraph(false);
    }
  };

  const loadSavedGraph = (savedGraph: any) => {
    try {
      const graphData = JSON.parse(savedGraph.graphData);
      setKnowledgeGraph(graphData);
      setShowVisualization(false);
    } catch (error) {
      console.error("Error loading saved graph:", error);
    }
  };

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleOpenTextExtractionDialog = async () => {
    console.log("conversation", conversation);
    if (conversation) {
      const convo =
        conversation.calls?.items?.map((call: any, index: number) => [
          {
            id: "prompt" + index,
            role: "user",
            content: call?.prompt,
          },

          /* memories are just from the customers inputs
          {
            id: "response" + index,
            role: "assistant",
            content: call.response,
          },*/
        ]) || [];

      console.log("convo", convo);

      const conversationText = JSON.stringify(convo, null, 2);
      setExtractionText(conversationText);

      // Open dialog first
      setTextExtractionDialogOpen(true);

      // Then extract memories with the conversation text
      await extractMemoriesFromText(conversationText);
    } else {
      setTextExtractionDialogOpen(true);
    }
  };

  const handleCloseTextExtractionDialog = () => {
    setTextExtractionDialogOpen(false);
    setExtractionText("");
    setExtractedMemories([]);
  };

  const extractMemoriesFromText = async (
    conversationText: string | null = null
  ) => {
    let analyzeText = conversationText || extractionText;

    setAnalyzingText(true);

    const prompt = `{{system_analyzetext}}
${analyzeText.trim()}`;

    console.log("Extracting memories from text:", prompt);

    try {
      const results = await send(prompt, [], [], false, false);
      console.log("Text extraction LLM response:", results);

      // Strip out markdown code block formatting
      let cleanedResults = results as string;
      cleanedResults = cleanedResults.replace(/```json\s*/g, "");
      cleanedResults = cleanedResults.replace(/```\s*/g, "");
      cleanedResults = cleanedResults.trim();

      const analysis = JSON.parse(cleanedResults);
      setExtractedMemories(analysis.extractedMemories || []);
      setAnalyzingText(false);
    } catch (error) {
      console.error("Error extracting memories from text:", error);
      setAnalyzingText(false);
    }
  };

  const approveExtractedMemory = (memory: any) => {
    createMemoryMutation.mutate({
      projectId: project?.id || "",
      customerId: selectedCustomer?.id || "",
      entityType: LLMMemoryEntityType.CUSTOMER,
      status: "ACTIVE",
      key: memory.key,
      data: memory.data,
      memoryType: memory.memoryType as any,
      tags: memory.tags || [],
    });

    // Remove the memory from extracted memories
    setExtractedMemories((prev) => prev.filter((m) => m !== memory));
  };

  const discardExtractedMemory = (memory: any) => {
    setExtractedMemories((prev) => prev.filter((m) => m !== memory));
  };

  if (!selectedCustomer) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Select a customer to view their Knowledge Graph
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with customer info */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selectedCustomer.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={`Status: ${selectedCustomer.status}`}
              size="small"
              color="primary"
            />
            <Chip
              label={`Tokens: ${selectedCustomer.tokensRemaining ?? 0}`}
              size="small"
            />
            <Chip
              label={`Calls: ${selectedCustomer.numberOfCallsMade ?? 0}`}
              size="small"
            />
          </Box>
        </Box>

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: "rgba(0, 0, 0, 0.08)" }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            <Tab
              icon={<MemoryIcon />}
              label={`Memories (${filteredMemories.length})`}
              iconPosition="start"
            />
            <Tab
              icon={<AccountTreeIcon />}
              label="Knowledge Graph"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Memory filters */}
        {tabValue === 0 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              size="small"
              options={["all", "ACTIVE", "PAUSED", "DELETED"]}
              value={memoryFilter}
              onChange={(e, newValue) => setMemoryFilter(newValue || "all")}
              renderInput={(params) => (
                <TextField {...params} label="Filter by status" />
              )}
              sx={{ width: 200 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={analyzeMemories}
              disabled={analyzingMemories || memories.length === 0}
              startIcon={<MemoryIcon />}
            >
              {analyzingMemories ? "Analyzing..." : "Analyze Memories"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenTextExtractionDialog}
              disabled={!selectedCustomer}
              startIcon={<TextSnippetIcon />}
              color="secondary"
            >
              {conversation
                ? "Extract Memories from Conversation"
                : "Extract Memories from Text"}
            </Button>
          </Box>
        )}

        {/* Knowledge graph controls */}
        {tabValue === 1 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={generateKnowledgeGraph}
              disabled={generatingGraph || memories.length === 0}
              startIcon={<AccountTreeIcon />}
            >
              {generatingGraph ? "Generating..." : "Generate Graph"}
            </Button>
            {knowledgeGraph && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowVisualization(!showVisualization)}
                  color="primary"
                >
                  {showVisualization ? "Hide Visualization" : "Visualize"}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setSaveConfirmDialogOpen(true)}
                  disabled={savingGraph}
                  color="success"
                >
                  {savingGraph ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setKnowledgeGraph(null);
                    setShowVisualization(false);
                  }}
                  color="secondary"
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleExportMenuClick}
                  color="info"
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={handleExportMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      exportGraphData();
                      handleExportMenuClose();
                    }}
                  >
                    Export Data (JSON)
                  </MenuItem>
                  {showVisualization && [
                    <MenuItem
                      key="svg"
                      onClick={() => {
                        exportVisualizationAsSVG();
                        handleExportMenuClose();
                      }}
                    >
                      Export Visualization (SVG)
                    </MenuItem>,
                    <MenuItem
                      key="png"
                      onClick={() => {
                        exportVisualizationAsPNG();
                        handleExportMenuClose();
                      }}
                    >
                      Export Visualization (PNG)
                    </MenuItem>,
                  ]}
                </Menu>
              </>
            )}
          </Box>
        )}

        {/* Content area */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {tabValue === 0 && (
            <>
              {memoriesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Loading memories...
                  </Typography>
                </Box>
              ) : memoriesError ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="error">
                    Error loading memories
                  </Typography>
                </Box>
              ) : filteredMemories.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {filteredMemories.map((memory: any) => {
                    const suggestion = memorySuggestions[memory.id];
                    return (
                      <Card
                        key={memory.id}
                        variant="outlined"
                        sx={{
                          opacity:
                            memory.status === "PAUSED"
                              ? 0.6
                              : memory.status === "DELETED"
                              ? 0.3
                              : 1,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": { boxShadow: 2 },
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2" color="primary">
                              {memory.key}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                sx={{ color: "text.disabled" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenMemoryDialog(memory);
                                }}
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  color:
                                    memory.status === "PAUSED"
                                      ? "text.secondary"
                                      : "text.disabled",
                                }}
                                onClick={() =>
                                  handleMemoryStatusUpdate(
                                    memory.id,
                                    memory.status === "PAUSED"
                                      ? "ACTIVE"
                                      : "PAUSED"
                                  )
                                }
                                disabled={
                                  memory.status === "DELETED" ||
                                  updateMemoryMutation.isPending
                                }
                                title={
                                  memory.status === "PAUSED"
                                    ? "Resume"
                                    : "Pause"
                                }
                              >
                                {memory.status === "PAUSED" ? (
                                  <PlayArrowIcon />
                                ) : (
                                  <PauseIcon />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: "text.disabled" }}
                                onClick={() =>
                                  handleMemoryStatusUpdate(memory.id, "DELETED")
                                }
                                disabled={
                                  memory.status === "DELETED" ||
                                  updateMemoryMutation.isPending
                                }
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {memory.data}
                          </Typography>

                          {/* Current tags and memory info */}
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              alignItems: "center",
                              mb: suggestion ? 2 : 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Status: {memory.status}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Type: {memory.memoryType}
                            </Typography>
                            {memory.tags &&
                              memory.tags.length > 0 &&
                              memory.tags.map((tag: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                  }}
                                />
                              ))}
                          </Box>

                          {/* AI Suggestions */}
                          {suggestion && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "rgba(25, 118, 210, 0.08)",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                  display: "block",
                                }}
                              >
                                AI Suggestions:
                              </Typography>

                              {/* Suggested Tags */}
                              {suggestion.suggestedTags &&
                                suggestion.suggestedTags.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mb: 1, display: "block" }}
                                    >
                                      Suggested additional tags:
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        mb: 1,
                                      }}
                                    >
                                      {suggestion.suggestedTags.map(
                                        (tag: string, index: number) => (
                                          <Chip
                                            key={index}
                                            label={tag}
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: "0.65rem",
                                              bgcolor: "rgba(76, 175, 80, 0.1)",
                                              color: "success.main",
                                              border:
                                                "1px solid rgba(76, 175, 80, 0.3)",
                                            }}
                                          />
                                        )
                                      )}
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        onClick={() =>
                                          applySuggestion(memory.id, "tags")
                                        }
                                      >
                                        Apply Tags
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() =>
                                          discardSuggestion(memory.id, "tags")
                                        }
                                      >
                                        Discard
                                      </Button>
                                    </Box>
                                  </Box>
                                )}

                              {/* Suggested Data Improvement */}
                              {suggestion.suggestedDataImprovement && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 1, display: "block" }}
                                  >
                                    Suggested data improvement:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      p: 1,
                                      bgcolor: "background.paper",
                                      borderRadius: 1,
                                      mb: 1,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {suggestion.suggestedDataImprovement}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={() =>
                                        applySuggestion(memory.id, "data")
                                      }
                                    >
                                      Apply Data
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() =>
                                        discardSuggestion(memory.id, "data")
                                      }
                                    >
                                      Discard
                                    </Button>
                                  </Box>
                                </Box>
                              )}

                              {/* Reasoning */}
                              {suggestion.reasoning && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  Reasoning: {suggestion.reasoning}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No memories found for this customer
                  </Typography>
                </Box>
              )}
            </>
          )}

          {tabValue === 1 && (
            <>
              {/* Saved Graph Tabs */}
              {savedGraphs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Tabs
                    value={selectedGraphTab}
                    onChange={(e, newValue) => {
                      setSelectedGraphTab(newValue);
                      if (newValue === 0) {
                        // Current/new graph - keep existing knowledgeGraph
                      } else {
                        // Load saved graph
                        const savedGraph = savedGraphs[newValue - 1];
                        loadSavedGraph(savedGraph);
                      }
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      label={knowledgeGraph ? "Current" : "New Graph"}
                      sx={{ minWidth: 100 }}
                    />
                    {savedGraphs.map((graph, index) => (
                      <Tab
                        key={graph.id}
                        label={`v${graph.version}`}
                        sx={{ minWidth: 60 }}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}

              {generatingGraph ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Generating knowledge graph...
                  </Typography>
                </Box>
              ) : knowledgeGraph ? (
                <Box sx={{ height: "100%" }}>
                  {showVisualization ? (
                    <Box
                      sx={{
                        height: "100%",
                        position: "relative",
                        minHeight: "500px",
                      }}
                    >
                      <Box
                        id="d3-graph-container"
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: "500px",
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          backgroundColor: "background.paper",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          backgroundColor: "background.paper",
                          padding: "4px 8px",
                          borderRadius: 1,
                        }}
                      >
                        Interactive Knowledge Graph - Wheel: zoom, Drag
                        background: pan, Drag nodes: reposition, Double-click:
                        reset
                      </Typography>

                      {/* Legend */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          backgroundColor: "background.paper",
                          padding: "8px",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: "bold", mb: 1, display: "block" }}
                        >
                          Entity Types
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {Array.from(
                            new Set(
                              knowledgeGraph.entities?.map(
                                (entity: any) => entity.type
                              ) || []
                            )
                          ).map((type: unknown) => {
                            const entityType = type as string;
                            const getNodeColor = (entityType: string) => {
                              const colors: Record<string, string> = {
                                person: "#1976d2",
                                company: "#9c27b0",
                                technology: "#2196f3",
                                preference: "#4caf50",
                                goal: "#ff9800",
                                concept: "#607d8b",
                              };
                              return colors[entityType] || "#757575";
                            };

                            return (
                              <Box
                                key={entityType}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: getNodeColor(entityType),
                                    border: "1px solid #333",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px" }}
                                >
                                  {entityType.charAt(0).toUpperCase() +
                                    entityType.slice(1)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {/* Insights Section */}
                      {knowledgeGraph.insights &&
                        knowledgeGraph.insights.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                              Customer Insights
                            </Typography>
                            {knowledgeGraph.insights.map(
                              (insight: string, index: number) => (
                                <Typography
                                  key={index}
                                  variant="body2"
                                  sx={{
                                    p: 1,
                                    mb: 1,
                                    bgcolor: "primary.light",
                                    color: "primary.contrastText",
                                    borderRadius: 1,
                                    fontStyle: "italic",
                                  }}
                                >
                                  💡 {insight}
                                </Typography>
                              )
                            )}
                          </Box>
                        )}

                      {/* Entities Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Entities ({knowledgeGraph.entities?.length || 0})
                        </Typography>
                        <Grid container spacing={2}>
                          {knowledgeGraph.entities?.map(
                            (entity: any, index: number) => (
                              <Grid item xs={12} sm={6} key={index}>
                                <Card
                                  variant="outlined"
                                  sx={{ height: "100%" }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Chip
                                        label={entity.type}
                                        size="small"
                                        sx={{ mr: 1 }}
                                        color={
                                          entity.type === "person"
                                            ? "primary"
                                            : entity.type === "company"
                                            ? "secondary"
                                            : entity.type === "technology"
                                            ? "info"
                                            : entity.type === "preference"
                                            ? "success"
                                            : entity.type === "goal"
                                            ? "warning"
                                            : "default"
                                        }
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {entity.label}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mb: 1 }}
                                    >
                                      {entity.description}
                                    </Typography>
                                    {entity.properties &&
                                      Object.keys(entity.properties).length >
                                        0 && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                          }}
                                        >
                                          {Object.entries(
                                            entity.properties
                                          ).map(
                                            (
                                              [key, value]: [string, any],
                                              propIndex: number
                                            ) => (
                                              <Chip
                                                key={propIndex}
                                                label={`${key}: ${value}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  fontSize: "0.65rem",
                                                  height: 18,
                                                }}
                                              />
                                            )
                                          )}
                                        </Box>
                                      )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            )
                          )}
                        </Grid>
                      </Box>

                      {/* Relationships Section */}
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Relationships (
                          {knowledgeGraph.relationships?.length || 0})
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {knowledgeGraph.relationships?.map(
                            (rel: any, index: number) => {
                              const sourceEntity =
                                knowledgeGraph.entities?.find(
                                  (e: any) => e.id === rel.source
                                );
                              const targetEntity =
                                knowledgeGraph.entities?.find(
                                  (e: any) => e.id === rel.target
                                );

                              return (
                                <Card
                                  key={index}
                                  variant="outlined"
                                  sx={{ p: 2 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Chip
                                      label={sourceEntity?.label || rel.source}
                                      size="small"
                                      color="primary"
                                    />
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flex: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ mx: 1 }}
                                      >
                                        →
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mx: 1 }}
                                      >
                                        {rel.label || rel.type}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mx: 1 }}
                                      >
                                        →
                                      </Typography>
                                    </Box>
                                    <Chip
                                      label={targetEntity?.label || rel.target}
                                      size="small"
                                      color="secondary"
                                    />
                                    {rel.strength && (
                                      <Chip
                                        label={`${Math.round(
                                          rel.strength * 100
                                        )}%`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                </Card>
                              );
                            }
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                  }}
                >
                  <AccountTreeIcon
                    sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No knowledge graph generated yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Generate Knowledge Graph" to analyze customer
                    memories
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Enhanced Add Memory Button */}
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenMemoryDialog()}
            disabled={!selectedCustomer}
            fullWidth
            size="large"
          >
            Add Memory
          </Button>
        </Box>
      </Box>

      {/* Enhanced Memory Dialog */}
      <Dialog
        open={memoryDialogOpen}
        onClose={handleCloseMemoryDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MemoryIcon />
            {editingMemory ? "Edit Memory" : "Add New Memory"}
            {selectedCustomer && (
              <Chip
                label={selectedCustomer.name}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Memory Key"
                value={memoryForm.key}
                onChange={(e) =>
                  setMemoryForm({ ...memoryForm, key: e.target.value })
                }
                fullWidth
                required
                helperText="A unique identifier for this memory"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Memory Type</InputLabel>
                <Select
                  value={memoryForm.memoryType}
                  label="Memory Type"
                  onChange={(e) =>
                    setMemoryForm({
                      ...memoryForm,
                      memoryType: e.target.value,
                    })
                  }
                >
                  <MenuItem value="PREFERENCE">🎯 Preference</MenuItem>
                  <MenuItem value="FACT">📋 Fact</MenuItem>
                  <MenuItem value="CONTEXT">🔍 Context</MenuItem>
                  <MenuItem value="INSTRUCTION">📝 Instruction</MenuItem>
                  <MenuItem value="FEEDBACK">💭 Feedback</MenuItem>
                  <MenuItem value="OTHER">📌 Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Memory Data"
                value={memoryForm.data}
                onChange={(e) =>
                  setMemoryForm({ ...memoryForm, data: e.target.value })
                }
                fullWidth
                multiline
                rows={6}
                required
                helperText="The actual memory content"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tags"
                value={memoryForm.tags}
                onChange={(e) =>
                  setMemoryForm({ ...memoryForm, tags: e.target.value })
                }
                fullWidth
                placeholder="personal, work, preference, important"
                helperText="Comma-separated tags for categorization"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseMemoryDialog} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSaveMemory}
            variant="contained"
            disabled={
              !memoryForm.key ||
              !memoryForm.data ||
              createMemoryMutation.isPending ||
              editMemoryMutation.isPending
            }
            size="large"
          >
            {editingMemory ? "Update Memory" : "Create Memory"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Text Extraction Dialog */}
      <Dialog
        open={textExtractionDialogOpen}
        onClose={handleCloseTextExtractionDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextSnippetIcon />
            {conversation
              ? "Extract from conversation"
              : "Extract Memories from Text"}
            {selectedCustomer && (
              <Chip
                label={selectedCustomer.name}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 120px)",
          }}
        >
          {conversation ? (
            extractedMemories.length === 0 && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                <CircularProgress /> Analyzing conversation text for memories...
              </Typography>
            )
          ) : (
            <TextField
              label="Text to Analyze"
              value={extractionText}
              onChange={(e) => setExtractionText(e.target.value)}
              fullWidth
              multiline
              rows={6}
              placeholder="Paste conversation transcripts, notes, emails, or any text containing customer information..."
              helperText="Enter any text containing customer information. The AI will analyze it and suggest relevant memories to store."
              sx={{ mb: 3, flexShrink: 0 }}
            />
          )}

          {extractedMemories.length > 0 && (
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pr: 1,
                ...(conversation ? {} : { maxHeight: "400px" }),
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <MemoryIcon />
                Suggested Memories ({extractedMemories.length})
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, flexShrink: 0 }}
              >
                Review and approve the memories extracted from your text. You
                can approve individual memories or discard ones that aren't
                relevant.
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pr: 1,
                  ...(conversation ? {} : { maxHeight: "400px" }),
                }}
              >
                {extractedMemories.map((memory, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{ minHeight: "200px" }}
                  >
                    <CardContent sx={{ p: 2, height: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{
                            fontWeight: "bold",
                            flex: 1,
                            mr: 2,
                            wordBreak: "break-word",
                            lineHeight: 1.3,
                          }}
                        >
                          {memory.key}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            onClick={() => approveExtractedMemory(memory)}
                            disabled={createMemoryMutation.isPending}
                          >
                            ✓ Approve
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => discardExtractedMemory(memory)}
                          >
                            ✗ Discard
                          </Button>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mb: 2,
                          height: "80px",
                          overflow: "auto",
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ lineHeight: 1.4, wordBreak: "break-word" }}
                        >
                          {memory.data}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                          mb: 2,
                        }}
                      >
                        <Chip
                          label={memory.memoryType}
                          size="medium"
                          color={
                            memory.memoryType === "PREFERENCE"
                              ? "success"
                              : memory.memoryType === "FACT"
                              ? "primary"
                              : memory.memoryType === "CONTEXT"
                              ? "info"
                              : memory.memoryType === "INSTRUCTION"
                              ? "warning"
                              : memory.memoryType === "FEEDBACK"
                              ? "secondary"
                              : "default"
                          }
                          sx={{ fontWeight: "bold" }}
                        />
                        {memory.tags &&
                          memory.tags.map((tag: string, tagIndex: number) => (
                            <Chip
                              key={tagIndex}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                          ))}
                      </Box>

                      {memory.reasoning && (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "info.light",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "info.main",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: "italic",
                              color: "info.contrastText",
                              wordBreak: "break-word",
                              lineHeight: 1.3,
                            }}
                          >
                            <strong>💡 AI Reasoning:</strong> {memory.reasoning}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 2, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    extractedMemories.forEach((memory) =>
                      approveExtractedMemory(memory)
                    );
                  }}
                  disabled={createMemoryMutation.isPending}
                  color="success"
                >
                  Approve All ({extractedMemories.length})
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setExtractedMemories([])}
                  color="error"
                >
                  Discard All
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseTextExtractionDialog} size="large">
            Close
          </Button>
          {!conversation && (
            <Button
              onClick={() => extractMemoriesFromText(null)}
              variant="contained"
              disabled={!extractionText.trim() || analyzingText}
              size="large"
              startIcon={<TextSnippetIcon />}
            >
              {analyzingText ? "Analyzing..." : "Analyze Text"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={saveConfirmDialogOpen}
        onClose={() => setSaveConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Knowledge Graph</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to save this knowledge graph?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Version:</strong> v
            {savedGraphs.length > 0
              ? Math.max(...savedGraphs.map((g) => g.version || 0)) + 1
              : 1}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Generated from:</strong> {memories.length} memories
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Entities:</strong> {knowledgeGraph?.entities?.length || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Relationships:</strong>{" "}
            {knowledgeGraph?.relationships?.length || 0}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setSaveConfirmDialogOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSaveKnowledgeGraph}
            variant="contained"
            disabled={savingGraph}
            size="large"
            startIcon={<SaveIcon />}
          >
            {savingGraph ? "Saving..." : "Save Graph"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MemoryAndKnowledgeGraph;
